import { useId, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import {
  budgetRatio,
  linePath,
  meterTone,
  nearestIndex,
  niceTicks,
  toNumber,
  xPositions,
  yPosition,
  type Point,
} from './charts-math.js';

/**
 * The console's charts — plain HTML and inline SVG, no library.
 *
 * Built to the dataviz method: the form follows the data's job (a ratio
 * against a limit is a meter, a magnitude comparison is a bar, a range per
 * item is a dumbbell, part-to-whole is one stacked bar, change over time is a
 * line); marks are thin with 4px rounded data-ends and 2px surface gaps; text
 * never wears the data colour; a legend is present whenever there are two or
 * more series; every value is also in a table on the same screen, so nothing
 * is gated behind a hover. Colours are the validated reference palette
 * (admin.css `--viz-*`), checked against the console's white cards.
 */

/** Percent for a CSS width, clamped so an over-budget meter fills, never overflows. */
function pct(ratio: number): string {
  return `${String(Math.max(0, Math.min(100, ratio * 100)).toFixed(1))}%`;
}

// ---------------------------------------------------------------------------
// Meter — one ratio against a limit
// ---------------------------------------------------------------------------

export function Meter({
  label,
  spend,
  budget,
  spendText,
  budgetText,
}: {
  label: string;
  spend: unknown;
  budget: unknown;
  spendText: string;
  budgetText: string;
}) {
  const ratio = budgetRatio(spend, budget);
  const tone = ratio === null ? 'none' : meterTone(ratio);
  const share = ratio === null ? null : Math.round(ratio * 100);
  return (
    <div className="viz-meter">
      <div className="viz-meter-head">
        <span>{label}</span>
        <strong>{spendText}</strong>
      </div>
      {ratio === null ? (
        <div className="viz-meter-track none" aria-hidden="true">
          <div className="viz-meter-fill" style={{ width: 0 }} />
        </div>
      ) : (
        <div
          className={`viz-meter-track ${tone}`}
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(100, share ?? 0)}
          aria-valuetext={`${String(share)}% of ${budgetText}`}
        >
          <div className="viz-meter-fill" style={{ width: pct(ratio) }} />
        </div>
      )}
      <div className="viz-meter-foot">
        {ratio === null ? (
          <span>No budget set</span>
        ) : (
          <span>
            {tone === 'bad' ? '⚠ ' : tone === 'warn' ? '△ ' : ''}
            {String(share)}% of {budgetText}
            {tone === 'bad' ? ' — over budget' : tone === 'warn' ? ' — close to budget' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bars — compare one magnitude across items
// ---------------------------------------------------------------------------

export interface BarRow {
  key: string;
  label: string;
  value: number | null;
  /** Rendered after the value, muted — e.g. a success rate. */
  hint?: string;
}

export function Bars({ rows, format, ariaLabel }: { rows: BarRow[]; format: (value: number) => string; ariaLabel: string }) {
  const max = Math.max(1, ...rows.map((row) => row.value ?? 0));
  return (
    <div className="viz-bars" role="img" aria-label={ariaLabel}>
      {rows.map((row) => (
        <div key={row.key} className="viz-bar-row" title={`${row.label}: ${row.value === null ? 'not reported' : format(row.value)}`}>
          <span className="viz-bar-label">{row.label}</span>
          <span className="viz-bar-track">
            {row.value !== null && <span className="viz-bar-fill" style={{ width: pct(row.value / max) }} />}
          </span>
          <span className="viz-bar-value">
            {row.value === null ? <span className="cr-muted">—</span> : format(row.value)}
            {row.hint !== undefined && <span className="viz-bar-hint"> {row.hint}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dumbbell — a low→high range per item
// ---------------------------------------------------------------------------

export interface DumbbellRow {
  key: string;
  label: string;
  lo: number | null;
  hi: number | null;
}

export function Dumbbell({
  rows,
  format,
  loLabel,
  hiLabel,
  ariaLabel,
}: {
  rows: DumbbellRow[];
  format: (value: number) => string;
  loLabel: string;
  hiLabel: string;
  ariaLabel: string;
}) {
  const max = Math.max(1, ...rows.flatMap((row) => [row.hi ?? 0, row.lo ?? 0]));
  return (
    <div className="viz-dumbbells" role="img" aria-label={ariaLabel}>
      <div className="viz-legend">
        <span className="viz-legend-item">
          <span className="viz-dot lo" aria-hidden="true" /> {loLabel}
        </span>
        <span className="viz-legend-item">
          <span className="viz-dot hi" aria-hidden="true" /> {hiLabel}
        </span>
      </div>
      {rows.map((row) => {
        const lo = row.lo === null ? null : (row.lo / max) * 100;
        const hi = row.hi === null ? null : (row.hi / max) * 100;
        return (
          <div
            key={row.key}
            className="viz-bar-row"
            title={`${row.label}: ${loLabel} ${row.lo === null ? '—' : format(row.lo)} · ${hiLabel} ${row.hi === null ? '—' : format(row.hi)}`}
          >
            <span className="viz-bar-label">{row.label}</span>
            <span className="viz-dumbbell-track">
              {lo !== null && hi !== null && (
                <span className="viz-dumbbell-bar" style={{ left: `${String(lo)}%`, width: `${String(Math.max(0, hi - lo))}%` }} />
              )}
              {lo !== null && <span className="viz-dot lo" style={{ left: `${String(lo)}%` }} />}
              {hi !== null && <span className="viz-dot hi" style={{ left: `${String(hi)}%` }} />}
            </span>
            <span className="viz-bar-value">
              {row.lo === null ? '—' : format(row.lo)} <span className="viz-bar-hint">→</span> {row.hi === null ? '—' : format(row.hi)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StackedBar — part-to-whole, one bar
// ---------------------------------------------------------------------------

export interface Segment {
  key: string;
  label: string;
  value: number;
}

/** The validated categorical order — slots 1–4, never cycled past what is passed. */
const SERIES = ['viz-c1', 'viz-c2', 'viz-c3', 'viz-c4', 'viz-c5', 'viz-c6', 'viz-c7', 'viz-c8'];

export function StackedBar({ segments, format, ariaLabel }: { segments: Segment[]; format: (value: number) => string; ariaLabel: string }) {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  return (
    <div className="viz-stack" role="img" aria-label={ariaLabel}>
      <div className="viz-stack-bar">
        {segments.map((segment, index) =>
          segment.value > 0 && total > 0 ? (
            <span
              key={segment.key}
              className={`viz-stack-seg ${SERIES[index] ?? 'viz-c8'}`}
              style={{ flexGrow: segment.value }}
              title={`${segment.label}: ${format(segment.value)}`}
            />
          ) : null,
        )}
      </div>
      <div className="viz-legend wrap">
        {segments.map((segment, index) => (
          <span key={segment.key} className="viz-legend-item">
            <span className={`viz-swatch ${SERIES[index] ?? 'viz-c8'}`} aria-hidden="true" />
            {segment.label} <strong>{format(segment.value)}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LineChart — one series over time
// ---------------------------------------------------------------------------

export interface TimePoint {
  at: number;
  value: number;
  /** What the tooltip says about this point, beyond the value. */
  note?: string;
}

// A flat frame: at full card width it scales to a comfortable height rather than a wall.
const W = 960;
const H = 190;
const PAD = { top: 12, right: 44, bottom: 26, left: 40 };
const TICKS = 4;

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function LineChart({
  points,
  format,
  ariaLabel,
  formatWhen,
}: {
  points: TimePoint[];
  format: (value: number) => string;
  ariaLabel: string;
  formatWhen: (ms: number) => string;
}) {
  const [active, setActive] = useState<number | null>(null);
  const id = useId();
  const sorted = [...points].sort((a, b) => a.at - b.at);
  const plot = { width: W - PAD.left - PAD.right, height: H - PAD.top - PAD.bottom };
  const values = sorted.map((point) => point.value);
  const ticks = niceTicks(Math.min(...values), Math.max(...values), TICKS);
  const box = { ...plot, yMin: ticks[0] ?? 0, yMax: ticks[ticks.length - 1] ?? 1 };
  const series: Point[] = sorted.map((point) => ({ x: point.at, y: point.value }));
  const xs = xPositions(series, plot.width);
  const path = linePath(series, box);
  const last = sorted[sorted.length - 1];

  const pick = (event: PointerEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * plot.width;
    setActive(nearestIndex(xs, x));
  };
  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (sorted.length === 0) return;
    if (event.key === 'ArrowRight') setActive((current) => Math.min(sorted.length - 1, (current ?? -1) + 1));
    else if (event.key === 'ArrowLeft') setActive((current) => Math.max(0, (current ?? sorted.length) - 1));
    else if (event.key === 'Escape') setActive(null);
    else return;
    event.preventDefault();
  };

  const activePoint = active === null ? undefined : sorted[active];
  const activeX = active === null ? undefined : xs[active];

  return (
    <div className="viz-line" tabIndex={0} onKeyDown={onKey} onBlur={() => setActive(null)} aria-describedby={`${id}-desc`}>
      <span id={`${id}-desc`} className="viz-sr">
        {ariaLabel}. Use the left and right arrow keys to move between points.
      </span>
      <svg viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={ariaLabel} className="viz-line-svg">
        <g transform={`translate(${String(PAD.left)},${String(PAD.top)})`}>
          {ticks.map((tick) => {
            const y = yPosition(tick, box);
            return (
              <g key={tick}>
                <line x1={0} x2={plot.width} y1={y} y2={y} className="viz-grid" />
                <text x={-8} y={y} className="viz-axis-text" textAnchor="end" dominantBaseline="middle">
                  {format(tick)}
                </text>
              </g>
            );
          })}
          <line x1={0} x2={plot.width} y1={plot.height} y2={plot.height} className="viz-baseline" />
          {sorted.length > 0 && (
            <>
              <text x={0} y={plot.height + 18} className="viz-axis-text" textAnchor="start">
                {shortDate(sorted[0]?.at ?? 0)}
              </text>
              {sorted.length > 1 && (
                <text x={plot.width} y={plot.height + 18} className="viz-axis-text" textAnchor="end">
                  {shortDate(last?.at ?? 0)}
                </text>
              )}
            </>
          )}
          <path d={path} className="viz-series" />
          {sorted.map((point, index) => (
            <circle
              key={point.at}
              cx={xs[index] ?? 0}
              cy={yPosition(point.value, box)}
              r={index === active ? 5.5 : 4}
              className="viz-marker"
            />
          ))}
          {last !== undefined && (
            <text
              x={(xs[xs.length - 1] ?? 0) + 10}
              y={yPosition(last.value, box)}
              className="viz-end-label"
              dominantBaseline="middle"
            >
              {format(last.value)}
            </text>
          )}
          {activeX !== undefined && <line x1={activeX} x2={activeX} y1={0} y2={plot.height} className="viz-crosshair" />}
          <rect
            x={0}
            y={0}
            width={plot.width}
            height={plot.height}
            fill="transparent"
            onPointerMove={pick}
            onPointerLeave={() => setActive(null)}
          />
        </g>
      </svg>
      {activePoint !== undefined && activeX !== undefined && (
        <div
          className="viz-tooltip"
          role="status"
          style={{
            left: `${String(((PAD.left + activeX) / W) * 100)}%`,
            transform: `translateX(${activeX > plot.width / 2 ? '-100%' : '0'})`,
          }}
        >
          <strong>{format(activePoint.value)}</strong>
          <span>{formatWhen(activePoint.at)}</span>
          {activePoint.note !== undefined && <span>{activePoint.note}</span>}
        </div>
      )}
    </div>
  );
}

/** A small caption under a chart, muted. */
export function ChartNote({ children }: { children: ReactNode }) {
  return <p className="viz-note">{children}</p>;
}

export { toNumber };
